import { authGet, authPut, authPost, authDelete } from "../../lib";
import { UIActions } from "../ui/actions";
import { format } from "date-fns";

// Case dimension fields (case_diameter, case_width, case_length, case_thickness,
// height_and_lugs, lug_width) are entered by admins as decimals (e.g., "41.5").
// Some columns are DOUBLE/DECIMAL(5,2), others are VARCHAR — inconsistent.
// This normalizes: parse to float, round to 2 decimals max, return null on
// empty/blank. Prevents both integer truncation (parseInt "41.5" -> 41) and
// runaway precision (28.5637943 landing in a DOUBLE column).
const toDecimal2 = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return Math.round(n * 100) / 100;
};

// Normalize a raw form value that MAY be an empty string into null. The swagger
// request-body schema declares many _id fields as `{ type: "number", nullable: true }`
// with `additionalProperties: false` at the top level. Empty string fails
// validation (not a number, not null). New watches start with `null` from
// initial form state; duplicates can carry "" when the loaded watch had no
// value for that field — that's the case that 400'd the duplicate flow.
const nullIfEmpty = (v) =>
  v === "" || v === undefined ? null : v;

const type = {
  GET_WATCH_LISTS: "GET_WATCH_LISTS",
  GET_ATTRIBUTES_LISTS: "GET_ATTRIBUTES_LISTS",
  GET_WATCH_LIST: "GET_WATCH_LIST",
  ADD_TO_WATCH_LIST: "ADD_TO_WATCH_LIST",
};

export const WatchActions = {
  type,

  getAllLists() {
    return async (dispatch, getState) => {
      const response = await authGet("/get-lists");
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load lists."));
        return;
      }
      dispatch({
        type: type.GET_WATCH_LISTS,
        payload: data,
      });
    };
  },
  getList(listTypes = []) {
    return async (dispatch, getState) => {
      const response = await authGet(["/get-lists", { listTypes }]);
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load list."));
        return;
      }
      dispatch({
        type: type.GET_WATCH_LIST,
        payload: data,
      });
    };
  },
  getAttribute(attribute, brand_id, siteId, search, page) {
    return async (dispatch, getState) => {
      let apiCall = {};
      // Paginate series ONLY when a `page` is passed (the manage-series grid).
      // Callers that omit page (e.g. the group transfer list) get the full array
      // back — same shape/behavior as before the pagination change.
      const seriesPaged =
        attribute === "series" && page !== undefined && page !== null;
      if (attribute === "series") {
        apiCall = { site: siteId, brand_id: brand_id };
        if (seriesPaged) {
          const pageSize = 100;
          apiCall.limit = pageSize;
          apiCall.offset = (page || 0) * pageSize;
        }
        if (search != null && search !== "") apiCall.q = search;
      } else {
        apiCall = { site: siteId };
      }
      const response = await authGet([
        `/manage-attributes/${attribute}`,
        apiCall,
      ]);
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load attribute."));
        return;
      }
      // Paginated series returns { rows, total }; everything else (incl. unpaginated
      // series) returns a plain array.
      const list = seriesPaged ? data?.rows ?? [] : data;
      dispatch({
        type: type.GET_ATTRIBUTES_LISTS,
        payload: list,
      });
      return data;
    };
  },
  // Super-admin Mass Update. dry_run:true returns { count } for the preview;
  // otherwise returns { updated, brand_id, site, changes }.
  massUpdate(siteId, payload) {
    return async (dispatch) => {
      const { data, error } = await authPost(
        `/mass-update?site=${siteId}`,
        payload,
      );
      if (error) {
        dispatch(UIActions.showError(error?.message || "Mass update failed."));
        return null;
      }
      return data;
    };
  },
  massPriceUpdate(siteId, payload) {
    return async (dispatch) => {
      const { data, error } = await authPost(
        `/mass-price-update?site=${siteId}`,
        payload,
      );
      if (error) {
        dispatch(UIActions.showError(error?.message || "Mass price update failed."));
        return null;
      }
      return data;
    };
  },
  getSiteAttribute(attribute, siteId) {
    return async (dispatch, getState) => {
      const response = await authGet([
        `/manage-site-attributes/${attribute}?site=${siteId}`,
      ]);
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load site attribute."));
        return;
      }
      dispatch({
        type: type.GET_ATTRIBUTES_LISTS,
        payload: data,
      });
      return data;
    };
  },
  getShippingAttribute(siteId) {
    return async (dispatch, getState) => {
      const response = await authGet([`/manage-shipping-tiers?site=${siteId}`]);
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load shipping attribute."));
        return;
      }
      dispatch({
        type: type.GET_ATTRIBUTES_LISTS,
        payload: data,
      });
      return data;
    };
  },
  getAttributeGroups(attribute, siteId) {
    return async (dispatch, getState) => {
      const response = await authGet([
        `/manage-attribute-groups/${attribute}`,
        { site: siteId ?? 0 },
      ]);
      const { data, error } = response;
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to load attribute groups."));
        return;
      }
      dispatch({
        type: type.GET_ATTRIBUTES_LISTS,
        payload: data,
      });
      return data;
    };
  },

  async getDetails(field, id, site, all) {
    const response = await authGet([
      `/details-watches`,
      { field: field, id: id, all: all, site: site },
    ]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getHistory(id, siteId) {
    const response = await authGet([`/item-history/${id}?site=${siteId}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getRecentHistory(site) {
    const response = await authGet([`records/recentlyAdded?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getRecentlyChanged(site) {
    const response = await authGet([`records/recentlyChanged?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getPriceNotSet(site) {
    const response = await authGet([`records/noPrice?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getStatusNotMatch(site) {
    const response = await authGet([`records/status?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getSpecialsNotMatch(site) {
    const response = await authGet([`records/specials?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getShippingDetails(id, site) {
    const response = await authGet([
      `/shipping-details-values/${id}?site=${site}`,
    ]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getDefaultShippingDetails(site) {
    const response = await authGet([`/shipping-details-defaults?site=${site}`]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },

  async getDetailsGroups(field, id, site, isAGroup, all) {
    const response = await authGet([
      `/details-groups`,
      { field: field, id: id, all: all, site: site },
    ]);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },

  async getDetailsValue(field, id, isAGroup, siteId) {
    const url = isAGroup ? `/group-details-value` : `/details-value`;
    const response = await authGet(
      `${url}/${id}?field=${field}&site=${siteId}`,
    );
    const { data, error } = response;
    if (error) {
      console.error('Failed to get details value:', error);
      return null;  // Return null instead of undefined
    }
    return data;
  },
  async getCalibreList() {
    const response = await authGet("calibre-list");
    const { data, error } = response;
    if (error) return null;
    return data;
  },
  async getMovementTypeList() {
    const response = await authGet(["/get-lists", { listTypes: ["calibre_movement_type"] }]);
    const { data, error } = response;
    if (error) return [];
    return data?.calibre_movement_type || [];
  },
  async getMovementDetails(id) {
    const response = await authGet(`calibre-details/${id}`);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },
  async getRegex(id) {
    const response = await authGet(`brand-regex?brand_id=${id}`);
    const { data, error } = response;
    if (error) {
      return null;
    }
    return data;
  },

  addDetails({
    rawAttribute,
    myValue,
    myBrandId,
    attribute,
    siteId,
    setNewFormik,
    silent,
  }) {
    return async (dispatch, getState) => {
      let values = {
        name: myValue,
        brand_id: myBrandId,
        site_id: siteId,
      };

      const { data, error } = await authPost(
        `/details-value?field=${rawAttribute}&site=${siteId}`,
        values,
      );
      if (setNewFormik) {
        setNewFormik(data);
      }

      if (error) {
        dispatch(
          UIActions.showError(
            error.message + ". " + myValue + " was not saved.",
          ),
        );
      } else {
        // const { scopes: { site_id } = {} } = getState();
        if (
          rawAttribute === "warranty" ||
          rawAttribute === "vendor" ||
          rawAttribute === "delivery"
        ) {
          dispatch(WatchActions.getSiteAttribute(rawAttribute, siteId));
        } else if (
          rawAttribute.slice(-6) === "groups" ||
          rawAttribute.slice(-5) === "group"
        ) {
          dispatch(WatchActions.getAttributeGroups(rawAttribute, null, siteId));
        } else if (rawAttribute === "brand") {
          dispatch(WatchActions.getAttribute(rawAttribute, null, siteId));
        } else if (rawAttribute === "brand") {
          dispatch(WatchActions.getAttribute(rawAttribute, null, siteId));
        } else {
          dispatch({
            type: type.ADD_TO_WATCH_LIST,
            payload: data,
          });
          dispatch(WatchActions.getAttribute(rawAttribute, null, siteId));
        }

        if (!silent) {
          dispatch(
            UIActions.showUpdated(
              attribute + " - " + myValue + " was successfully added",
            ),
          );
        }
      }
    };
  },
  addShipping({ myValue, attribute, siteId }) {
    return async (dispatch, getState) => {
      const { data, error } = await authPost(
        `manage-shipping-tiers?site=${siteId}`,
        {
          name: myValue,
        },
      );
      if (error) {
        dispatch(
          UIActions.showError(
            error.message + ". " + myValue + " was not saved.",
          ),
        );
      } else {
        // const { scopes: { site_id } = {} } = getState();
        dispatch(WatchActions.getShippingAttribute(siteId));

        dispatch({
          type: type.ADD_TO_WATCH_LIST,
          payload: data,
        });
        dispatch(
          UIActions.showUpdated(
            attribute + " - " + myValue + " was successfully added",
          ),
        );
      }
    };
  },

  editBrand(key, groupDetailsData, deactivate_brand) {
    return async (dispatch, getState) => {
      const { data, error } = await authPut(key, {
        brand: groupDetailsData.brand,
        // Coerce the boolean toggles: the swagger BoolOptions schema is
        // anyOf[boolean|integer] and rejects null, so a brand whose hide/
        // deactivate_brand is NULL in the DB would 400 the whole save.
        hide: !!groupDetailsData.hide,
        deactivate_brand: !!deactivate_brand,
        brand_page_name: groupDetailsData.brand_page_name,
        page_title: groupDetailsData.page_title,
        meta_description: groupDetailsData.meta_description,
        meta_keywords: groupDetailsData.meta_keywords,
        meta_headers: groupDetailsData.meta_headers,
        stub: groupDetailsData.stub,
        title: groupDetailsData.sites_router_metum?.title,
        description: groupDetailsData.sites_router_metum?.description,
        keywords: groupDetailsData.sites_router_metum?.keywords,
        headers: groupDetailsData.sites_router_metum?.headers,
        custom_aka: !!groupDetailsData.custom_aka,
        prefix: groupDetailsData.aka_rule?.prefix,
        suffix: groupDetailsData.aka_rule?.suffix,
        strip_chars: groupDetailsData.aka_rule?.strip_chars,
      });

      if (error) {
        dispatch(
          UIActions.showError(error.message + ". Brand was not saved."),
        );
        return;
      }
      dispatch(
        UIActions.showUpdated("Success: Brand was successfully updated"),
      );
    };
  },
  editMovement(key, movementData) {
    return async (dispatch, getState) => {
      const { data, error } = await authPut(key, movementData);
      if (error) {
        dispatch(
          UIActions.showError(
            error.message + ". Movement details  was not saved.",
          ),
        );
      }
      const { scopes: { site_id } = {} } = getState();
      dispatch(WatchActions.getAttribute("calibre", null, site_id));
      dispatch(
        UIActions.showUpdated("Success: movement was successfully updated"),
      );
    };
  },
  editSeries(key, groupDetailsData, myBrandId) {
    return async (dispatch, getState) => {
      const { data, error } = await authPut(key, {
        name: groupDetailsData.series,
        brand_id: myBrandId,
        stub: groupDetailsData.stub,
        title: groupDetailsData.sites_router_metum?.title,
        description: groupDetailsData.sites_router_metum?.description,
        keywords: groupDetailsData.sites_router_metum?.keywords,
        headers: groupDetailsData.sites_router_metum?.headers,
      });
      if (error) {
        dispatch(
          UIActions.showError(error.message + ". Brand SEO  was not saved."),
        );
      }
      const { scopes: { site_id } = {} } = getState();
      dispatch(WatchActions.getAttribute("series", null, site_id));
      dispatch(
        UIActions.showUpdated("Success: Series SEO was successfully updated"),
      );
    };
  },

  editWatch(key, watchValues) {
    console.log("EDIT-", watchValues);
    console.log("before-", watchValues.vendor_exp);
    watchValues.vendor_exp
      ? (watchValues.vendor_exp = format(
          new Date(watchValues.vendor_exp),
          "yyyy-MM-dd",
        ))
      : (watchValues.vendor_exp = null);
    console.log("after-", watchValues.vendor_exp);
    const restructuredWatchValues = {
      date_added: watchValues.date_added,
      band_color_id: watchValues.band_color_id,
      band_material_id: watchValues.band_material_id,
      band_name_id: watchValues.band_name_id,
      band_notes: watchValues.band_notes,
      general_band_notes: watchValues.general_band_notes,
      bezel_characteristics_ids: watchValues.bezel_characteristics_ids,
      bezel_material_ids: watchValues.bezel_material_ids,
      bezel_color_id: watchValues.bezel_color_id,
      case_color_id: watchValues.case_color_id,
      bezel_finish_id: watchValues.bezel_finish_id,
      bezel_id: watchValues.bezel_id,
      bezel_material_id: watchValues.bezel_material_id,
      bezel_notes: watchValues.bezel_notes,
      brand_id: watchValues.brand_id,
      button_material_id: parseInt(watchValues.button_material_id),
      button_notes: watchValues.button_notes,
      calibre_id: nullIfEmpty(watchValues.calibre_id),
      calibre_name: watchValues.calibre_name || null,
      case_back_id: watchValues.case_back_id,
      case_back_notes: watchValues.case_back_notes,
      case_diameter: toDecimal2(watchValues.case_diameter),
      case_finish_id: parseInt(watchValues.case_finish_id),
      case_finish_notes: watchValues.case_finish_notes,
      general_case_notes: watchValues.general_case_notes,
      case_length: toDecimal2(watchValues.case_length),
      case_material_id: watchValues.case_material_id,
      case_shape_id: watchValues.case_shape_id,
      case_thickness: toDecimal2(watchValues.case_thickness),
      case_width: toDecimal2(watchValues.case_width),
      clasp_characteristic_id: watchValues.clasp_characteristic_id,
      clasp_characteristic_ids: watchValues.clasp_characteristic_ids,
      clasp_material_ids: watchValues.clasp_material_ids,
      clasp_name_id: watchValues.clasp_name_id,
      clasp_notes: watchValues.clasp_notes,
      clasp_type_id: watchValues.clasp_type_id,
      crown_material_id: parseInt(watchValues.crown_material_id),
      crown_material_ids: watchValues.crown_material_ids,
      crown_notes: watchValues.crown_notes,
      crystal_id: watchValues.crystal_id,
      crystal_notes: watchValues.crystal_notes,
      date_display: watchValues.date_display || false,
      date_indicator_id: watchValues.date_indicator_id,
      date_location_id: watchValues.date_location_id,
      date_reduced: watchValues.date_reduced,
      date_notes: watchValues.date_notes,
      delivery_id: watchValues.delivery_id,
      description: watchValues.description,
      show_description: watchValues.show_description ? 1 : 0,
      dial_color_id: watchValues.dial_color_id,
      dial_finish_id: parseInt(watchValues.dial_finish_id),
      dial_flange_color_id: watchValues.dial_flange_color_id,
      dimension_notes: watchValues.dimension_notes,
      edition_notes: watchValues.edition_notes,
      flange_indication_id: watchValues.flange_indication_id,
      gtin: watchValues.gtin,
      hands_color_id: watchValues.hands_color_id,
      hands_finish_id: watchValues.hands_finish_id,
      hands_luminescence: watchValues.hands_luminescence || false,
      hands_luminescence_notes: watchValues.hands_luminescence_notes,
      hands_material_id: watchValues.hands_material_id,
      hands_notes: watchValues.hands_notes,
      height_and_lugs: toDecimal2(watchValues.height_and_lugs),
      helium_escape_valve: watchValues.helium_escape_valve || false,
      helium_escape_valve_notes: watchValues.helium_escape_valve_notes,
      hour_marker_id: watchValues.hour_marker_id,
      hour_marker_color_id: watchValues.hour_marker_color_id,
      hour_marker_finish_id: watchValues.hour_marker_finish_id,
      hour_marker_material_id: watchValues.hour_marker_material_id,
      hour_marker_luminescence: watchValues.hour_marker_luminescence || false,
      hour_marker_luminescence_notes:
        watchValues.hour_marker_luminescence_notes,
      hour_marker_notes: watchValues.hour_marker_notes,
      hour_marker_type_id: watchValues.hour_marker_type_id,
      item_additional_band_refs: watchValues.item_additional_band_refs,
      is_limited: watchValues.is_limited || false,
      is_numbered: watchValues.is_numbered || false,
      limited_qty: parseInt(watchValues.limited_qty),
      lug_width: toDecimal2(watchValues.lug_width),
      luminescence_id: watchValues.luminescence_id,
      luminescence_notes: watchValues.luminescence_notes,
      main_dial_notes: watchValues.main_dial_notes,
      model_aka: watchValues.model_aka,
      model_no: watchValues.model_no,
      model_no_1: watchValues.model_no_1,
      model_no_2: watchValues.model_no_2,
      numbered_edition: watchValues.numbered_edition || false,
      papers_included: watchValues.papers_included || false,
      price_on_special: watchValues.price_on_special || false,
      price_our:
        parseInt(watchValues.price_our?.toString().replace(/,/g, "")) || 0,
      price_retail:
        parseInt(watchValues.price_retail?.toString().replace(/,/g, "")) || 0,
      price_special:
        parseInt(watchValues.price_special?.toString().replace(/,/g, "")) || 0,
      retired: watchValues.retired ? 1 : 0,
      screw_down_buttons: watchValues.screw_down_buttons || false,
      screw_down_crown: watchValues.screw_down_crown || false,
      second_hand_id: watchValues.second_hand_id,
      second_hand_color_id: watchValues.second_hand_color_id,
      second_hand_luminescence: watchValues.second_hand_luminescence || false,
      second_hand_material_id: watchValues.second_hand_material_id,
      second_hand_notes: watchValues.second_hand_notes,
      seo_stub: watchValues.seo_stub,
      series_id: watchValues.series_id,
      ship_tier_id: watchValues.ship_tier_id,
      show_recent: watchValues.show_recent ? 1 : 0,
      show_reduced: watchValues.show_reduced,
      show_display_date: watchValues.show_display_date ? 1 : 0,
      display_date: watchValues.display_date
        ? format(new Date(watchValues.display_date), "yyyy-MM") + "-01"
        : null,
      display_date_hide_month: watchValues.display_date_hide_month ? 1 : 0,
      hide_retail: watchValues.hide_retail ? 1 : 0,
      tpc_sync_enabled: watchValues.tpc_sync_enabled ? 1 : 0,
      size_id: watchValues.size_id,
      is_special_edition: watchValues.is_special_edition || false,
      special_edition_notes: watchValues.special_edition_notes,
      status_id: watchValues.status_id,
      subdial_color_id: parseInt(watchValues.subdial_color_id),
      subdial_notes: watchValues.subdial_notes,
      title: watchValues.title,
      track_notes: watchValues.track_notes,
      track_type_id: watchValues.track_type_id,
      vendor_id: watchValues.vendor_id,
      vendor_exp: watchValues.vendor_exp,
      warranty_id: watchValues.warranty_id,
      water_resistance_m: parseInt(watchValues.water_resistance_m),
      water_resistance_ft: parseInt(watchValues.water_resistance_ft),
      case_atm: parseFloat(watchValues.case_atm),
      hz: watchValues.hz !== "" && watchValues.hz !== null ? parseFloat(watchValues.hz) : null,
      vph: watchValues.vph !== "" && watchValues.vph !== null ? parseInt(watchValues.vph) : null,
      jewels: watchValues.jewels !== "" && watchValues.jewels !== null ? parseInt(watchValues.jewels) : null,
      components: watchValues.components !== "" && watchValues.components !== null ? parseInt(watchValues.components) : null,
      reserve_hours: watchValues.reserve_hours !== "" && watchValues.reserve_hours !== null ? parseFloat(watchValues.reserve_hours) : null,
      reserve_days: watchValues.reserve_days !== "" && watchValues.reserve_days !== null ? parseFloat(watchValues.reserve_days) : null,
      reserve_notes: watchValues.reserve_notes || null,
      in_house: watchValues.in_house !== null && watchValues.in_house !== undefined ? parseInt(watchValues.in_house) : null,
      base_calibre: watchValues.base_calibre !== "" && watchValues.base_calibre !== null ? parseInt(watchValues.base_calibre) : null,
      movement_type_id: watchValues.movement_type_id !== "" && watchValues.movement_type_id !== null ? parseInt(watchValues.movement_type_id) : null,
      movement_notes: watchValues.movement_notes || null,
      // null-preserving like in_house — NULL means "linked, inherit from
      // calibre"; coercing to 0 would silently detach linked watches on save
      eol: watchValues.eol !== null && watchValues.eol !== undefined ? (watchValues.eol ? 1 : 0) : null,
      eol_notes: watchValues.eol_notes || null,
      item_complications: (watchValues.item_complications || []).map((c) => ({
        complication_id: c.complication_id,
        details: c.details || null,
      })),
      item_certification_ids: watchValues.item_certification_ids || [],
    };
    return async (dispatch, getState) => {
      const { data, error } = await authPut(key, restructuredWatchValues);
      if (error) {
        dispatch(
          UIActions.showError(error.message + ". Watch  was not Edited."),
        );
      } else {
        const { scopes: { site_id } = {} } = getState();
        dispatch(
          UIActions.showUpdated(restructuredWatchValues.title + ` Saved!  `),
        );

        return data;
      }
    };
  },
  addWatch(key, watchValues, duplicate) {
    console.log("ADD-", watchValues);
    watchValues.vendor_exp
      ? (watchValues.vendor_exp = format(
          new Date(watchValues.vendor_exp),
          "yyyy-MM-dd",
        ))
      : (watchValues.vendor_exp = null);
    const restructuredWatchValues = {
      // Date Recent for new adds/duplicates is set authoritatively on the
      // backend (= act_date_added, the creation timestamp), so whatever is sent
      // here is overridden. Left as a plain pass-through.
      date_added: watchValues.date_added,
      band_color_id: watchValues.band_color_id,
      band_material_id: watchValues.band_material_id,
      band_name_id: watchValues.band_name_id,
      band_notes: watchValues.band_notes,
      general_band_notes: watchValues.general_band_notes,
      bezel_characteristics_ids: watchValues.bezel_characteristics_ids,
      bezel_material_ids: watchValues.bezel_material_ids,
      bezel_color_id: watchValues.bezel_color_id,
      case_color_id: watchValues.case_color_id,
      bezel_finish_id: watchValues.bezel_finish_id,
      bezel_id: watchValues.bezel_id,
      bezel_material_id: watchValues.bezel_material_id,
      bezel_notes: watchValues.bezel_notes,
      brand_id: watchValues.brand_id,
      button_material_id: parseInt(watchValues.button_material_id),
      button_notes: watchValues.button_notes,
      calibre_id: nullIfEmpty(watchValues.calibre_id),
      calibre_name: watchValues.calibre_name || null,
      case_back_id: watchValues.case_back_id,
      case_back_notes: watchValues.case_back_notes,
      case_diameter: toDecimal2(watchValues.case_diameter),
      case_finish_id: parseInt(watchValues.case_finish_id),
      case_finish_notes: watchValues.case_finish_notes,
      general_case_notes: watchValues.general_case_notes,
      case_length: toDecimal2(watchValues.case_length),
      case_material_id: watchValues.case_material_id,
      case_shape_id: watchValues.case_shape_id,
      case_thickness: toDecimal2(watchValues.case_thickness),
      case_width: toDecimal2(watchValues.case_width),
      clasp_characteristic_id: watchValues.clasp_characteristic_id,
      clasp_characteristic_ids: watchValues.clasp_characteristic_ids,
      clasp_material_ids: watchValues.clasp_material_ids,
      clasp_name_id: watchValues.clasp_name_id,
      clasp_notes: watchValues.clasp_notes,
      clasp_type_id: watchValues.clasp_type_id,
      crown_material_id: parseInt(watchValues.crown_material_id),
      crown_material_ids: watchValues.crown_material_ids,
      crown_notes: watchValues.crown_notes,
      crystal_id: watchValues.crystal_id,
      crystal_notes: watchValues.crystal_notes,
      date_display: watchValues.date_display || false,
      date_indicator_id: watchValues.date_indicator_id,
      date_location_id: watchValues.date_location_id,
      date_reduced: watchValues.date_reduced,
      date_notes: watchValues.date_notes,
      delivery_id: watchValues.delivery_id,
      description: watchValues.description,
      show_description: watchValues.show_description ? 1 : 0,
      dial_color_id: watchValues.dial_color_id,
      dial_finish_id: parseInt(watchValues.dial_finish_id),
      dial_flange_color_id: watchValues.dial_flange_color_id,
      dimension_notes: watchValues.dimension_notes,
      edition_notes: watchValues.edition_notes,
      flange_indication_id: watchValues.flange_indication_id,
      gtin: duplicate ? null : watchValues.gtin,
      hands_color_id: watchValues.hands_color_id,
      hands_finish_id: watchValues.hands_finish_id,
      hands_luminescence: watchValues.hands_luminescence || false,
      hands_luminescence_notes: watchValues.hands_luminescence_notes,
      hands_material_id: watchValues.hands_material_id,
      hands_notes: watchValues.hands_notes,
      height_and_lugs: toDecimal2(watchValues.height_and_lugs),
      helium_escape_valve: watchValues.helium_escape_valve || false,
      helium_escape_valve_notes: watchValues.helium_escape_valve_notes,
      hour_marker_id: watchValues.hour_marker_id,
      hour_marker_color_id: watchValues.hour_marker_color_id,
      hour_marker_finish_id: watchValues.hour_marker_finish_id,
      hour_marker_material_id: watchValues.hour_marker_material_id,
      hour_marker_luminescence: watchValues.hour_marker_luminescence || false,
      hour_marker_luminescence_notes:
        watchValues.hour_marker_luminescence_notes,
      hour_marker_notes: watchValues.hour_marker_notes,
      hour_marker_type_id: watchValues.hour_marker_type_id,
      item_additional_band_refs: watchValues.item_additional_band_refs,
      is_limited: watchValues.is_limited || false,
      is_numbered: watchValues.is_numbered || false,
      limited_qty: parseInt(watchValues.limited_qty),
      lug_width: toDecimal2(watchValues.lug_width),
      luminescence_id: watchValues.luminescence_id,
      luminescence_notes: watchValues.luminescence_notes,
      main_dial_notes: watchValues.main_dial_notes,
      model_aka: watchValues.model_aka,
      model_no: watchValues.model_no,
      model_no_1: watchValues.model_no_1,
      model_no_2: watchValues.model_no_2,
      numbered_edition: watchValues.numbered_edition || false,
      papers_included: watchValues.papers_included || false,
      price_on_special: watchValues.price_on_special || false,
      price_our:
        parseInt(watchValues.price_our?.toString().replace(/,/g, "")) || 0,
      price_retail:
        parseInt(watchValues.price_retail?.toString().replace(/,/g, "")) || 0,
      price_special:
        parseInt(watchValues.price_special?.toString().replace(/,/g, "")) || 0,
      retired: watchValues.retired ? 1 : 0,
      screw_down_buttons: watchValues.screw_down_buttons || false,
      screw_down_crown: !!watchValues.screw_down_crown,
      second_hand_id: watchValues.second_hand_id,
      second_hand_color_id: watchValues.second_hand_color_id,
      second_hand_luminescence: watchValues.second_hand_luminescence || false,
      second_hand_material_id: watchValues.second_hand_material_id,
      second_hand_notes: watchValues.second_hand_notes,
      seo_stub: watchValues.seo_stub,
      series_id: watchValues.series_id,
      ship_tier_id: watchValues.ship_tier_id,
      show_recent: watchValues.show_recent ? 1 : 0,
      show_reduced: watchValues.show_reduced,
      show_display_date: watchValues.show_display_date ? 1 : 0,
      display_date: watchValues.display_date
        ? format(new Date(watchValues.display_date), "yyyy-MM") + "-01"
        : null,
      display_date_hide_month: watchValues.display_date_hide_month ? 1 : 0,
      hide_retail: watchValues.hide_retail ? 1 : 0,
      tpc_sync_enabled: watchValues.tpc_sync_enabled ? 1 : 0,
      size_id: watchValues.size_id,
      is_special_edition: watchValues.is_special_edition || false,
      special_edition_notes: watchValues.special_edition_notes,
      status_id: watchValues.status_id,
      subdial_color_id: parseInt(watchValues.subdial_color_id),
      subdial_notes: watchValues.subdial_notes,
      title: watchValues.title,
      track_notes: watchValues.track_notes,
      track_type_id: watchValues.track_type_id,
      vendor_id: watchValues.vendor_id,
      vendor_exp: watchValues.vendor_exp,
      warranty_id: watchValues.warranty_id,
      water_resistance_m: parseInt(watchValues.water_resistance_m),
      water_resistance_ft: parseInt(watchValues.water_resistance_ft),
      case_atm: parseFloat(watchValues.case_atm),
      movement_type_id: watchValues.movement_type_id !== "" && watchValues.movement_type_id !== null ? parseInt(watchValues.movement_type_id) : null,
      eol: watchValues.eol !== null && watchValues.eol !== undefined ? (watchValues.eol ? 1 : 0) : null,
      eol_notes: watchValues.eol_notes || null,
    };
    return async (dispatch) => {
      const { data, error } = await authPost(key, restructuredWatchValues);
      if (error) {
        dispatch(
          UIActions.showError(error.message + ". Watch  was not Added."),
        );
      } else {
        return data;
      }
    };
  },
  addImage(key, imageArray) {
    console.log("ADD Image-", imageArray);
    return async (dispatch, getState) => {
      const { data, error } = await authPost(key, imageArray);
      if (error) {
        dispatch(
          UIActions.showError(error.message + ". Image  was not Added."),
        );
      } else {
        const { scopes: { site_id } = {} } = getState();
        dispatch(UIActions.showUpdated("Image Added"));
        return data;
      }
    };
  },
  // Inline toggle from the Brands grid — flips a brand's per-brand TPC "Share"
  // create-time default (item_brand.tpc_sync_default). Returns true on success.
  toggleBrandTpcSync(brandId, value) {
    return async (dispatch) => {
      const { error } = await authPut(`/brand-tpc-sync/${brandId}`, {
        tpc_sync_default: value ? 1 : 0,
      });
      if (error) {
        dispatch(UIActions.showError(error?.message || "Failed to update TPC share default."));
        return false;
      }
      dispatch(UIActions.showUpdated("TPC share default updated"));
      return true;
    };
  },
  editDetails({ rawAttribute, id, myValue, myBrandId, category, attribute, siteId }) {
    return async (dispatch, getState) => {
      const { data, error } = await authPut(
        `/details-value/${id}?field=${rawAttribute}&site=${siteId}`,
        {
          name: myValue,
          brand_id: myBrandId,
          category,
          site_id: siteId,
        },
      );
      if (error) {
        dispatch(
          UIActions.showError(
            error.message + ". " + myValue + " was not saved.",
          ),
        );
      }
      // const { scopes: { site_id } = {} } = getState();
      // dispatch(WatchActions.getAttribute(rawAttribute, id, site_id));
      if (
        rawAttribute === "warranty" ||
        rawAttribute === "vendor" ||
        rawAttribute === "delivery"
      ) {
        dispatch(WatchActions.getSiteAttribute(rawAttribute, siteId));
      } else if (
        rawAttribute.slice(-6) === "groups" ||
        rawAttribute.slice(-5) === "group"
      ) {
        dispatch(WatchActions.getAttributeGroups(rawAttribute, siteId));
      } else dispatch(WatchActions.getAttribute(rawAttribute, id, siteId));
      dispatch(
        UIActions.showUpdated(
          attribute + " - " + myValue + "was successfully updated",
        ),
      );
    };
  },
  shipDetails({ id, shipData, siteId, myValue }) {
    return async (dispatch, getState) => {
      if (myValue === "ship_tier") {
        const { data, error } = await authPut(
          `/shipping-details-defaults?site=${siteId}`,
          shipData,
        );
        if (error) {
          dispatch(
            UIActions.showError(error.message + ". Ship Tier was not saved."),
          );
        }
      } else {
        const { data, error } = await authPut(
          `/shipping-details-values/${id}?site=${siteId}`,
          {
            ship_tier_id: id,
            ship_tier: myValue,
            data: shipData.data,
          },
        );
        if (error) {
          dispatch(
            UIActions.showError(error.message + ". Ship Tier was not saved."),
          );
        }
      }
      dispatch(WatchActions.getShippingAttribute(siteId));
      dispatch(
        UIActions.showUpdated("Success: Ship Tier was successfully updated"),
      );
    };
  },
  saveDetails({ groupDetailsData, right, key, attribute }) {
    return async (dispatch, getState) => {
      const { data, error } = await authPut(key, {
        name: groupDetailsData?.[attribute],
        brand_id: groupDetailsData?.brand_id,
        selected_list: right,
        stub: groupDetailsData?.stub,
        title: groupDetailsData?.sites_router_metum?.title,
        description: groupDetailsData?.sites_router_metum?.description,
        keywords: groupDetailsData?.sites_router_metum?.keywords,
        headers: groupDetailsData?.sites_router_metum?.headers,
      });
      if (error) {
        dispatch(
          UIActions.showError(error.message + ".  + newName +  was not saved."),
        );
      }
      const { scopes: { site_id } = {} } = getState();
      dispatch(WatchActions.getAttributeGroups(attribute));
      dispatch(
        UIActions.showUpdated(
          "Success: Series Groups SEO was successfully updated",
        ),
      );
    };
  },

  deleteDetails({ rawAttribute, id, myValue, attribute, siteId }) {
    return async (dispatch, getState) => {
      if (
        rawAttribute.slice(-6) === "groups" ||
        rawAttribute.slice(-5) === "group"
      ) {
        const { data, error } = await authDelete(
          `/group-details-value/${id}?field=${rawAttribute}&site=${siteId}`,
        );
        if (error) {
          dispatch(
            UIActions.showError(
              error.message + ". " + myValue + " was not saved.",
            ),
          );
        }
      } else if (rawAttribute === "calibre") {
        const { data, error } = await authDelete(`/calibre-details/${id}`);
        if (error) {
          dispatch(UIActions.showError("ERROR calibre Could not be deleted ."));
        }
      } else if (rawAttribute === "ship_tier") {
        const { data, error } = await authDelete(
          `shipping-details-values/${id}`, //missing site
        );
        if (error) {
          dispatch(
            UIActions.showError("ERROR Ship Tier could not be deleted ."),
          );
        }
      } else {
        const { data, error } = await authDelete(
          `/details-value/${id}?field=${rawAttribute}&site=${siteId}`,
        );
        if (error) {
          dispatch(
            UIActions.showError(
              error.message + ". " + myValue + " was not deleted.",
            ),
          );
        }
      }

      // const { scopes: { site_id } = {} } = getState();
      if (
        rawAttribute === "warranty" ||
        rawAttribute === "vendor" ||
        rawAttribute === "delivery"
      ) {
        dispatch(WatchActions.getSiteAttribute(rawAttribute, siteId));
      } else if (
        rawAttribute.slice(-6) === "groups" ||
        rawAttribute.slice(-5) === "group"
      ) {
        dispatch(WatchActions.getAttributeGroups(rawAttribute, siteId));
      } else {
        dispatch(WatchActions.getAttribute(rawAttribute, null, siteId));
      }
      dispatch(
        UIActions.showUpdated(
          attribute + " - " + myValue + " was successfully deleted",
        ),
      );
    };
  },
  addUser(form) {
    return async (dispatch, getState) => {
      const { data, error } = await authPost(`/auth/user`, {
        first_name: form.first_name,
        last_name: form.last_name,
        role_id: form.role_id,
        email: form.email,
      });
      if (error) {
        dispatch(UIActions.showError(error.message + ". User was not added."));
      } else {
        window.location.reload(true);
        dispatch(
          UIActions.showUpdated(
            "Success a email has been sent to " + form.email,
          ),
        );
      }
    };
  },
  deleteUser(id) {
    let object_id = { id: id };
    return async (dispatch, getState) => {
      const { data, error } = await authDelete(`/auth/user/${id}`);
      if (error) {
        dispatch(
          UIActions.showError(error.message + ". User was not deleted."),
        );
      } else {
        window.location.reload(true);
        dispatch(
          UIActions.showUpdated("Success User was successfully deleted"),
        );
      }
    };
  },
};
